import React, { useEffect, useState } from "react";
import api from "../../api";
import { toast } from "sonner";
import { Video, Plus, Loader2, Save, Trash2, X, ExternalLink } from "lucide-react";
import JidnyasaAttachments from "./JidnyasaAttachments";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const STATUSES = ["upcoming", "live", "past", "cancelled"];

function toLocalInputValue(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInputValue(local) {
  if (!local) return "";
  return new Date(local).toISOString();
}

function SessionForm({ session, onSaved, onClose }) {
  const isEdit = !!session?.id;
  const [form, setForm] = useState({
    topic: session?.topic || "",
    description: session?.description || "",
    scheduled_at: toLocalInputValue(session?.scheduled_at) || "",
    duration_minutes: session?.duration_minutes || 60,
    meeting_url: session?.meeting_url || "",
    recording_url: session?.recording_url || "",
    materials_url: session?.materials_url || "",
    status: session?.status || "upcoming",
    published: session?.published !== false,
  });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.topic.trim()) { toast.error("Topic required"); return; }
    if (!form.scheduled_at) { toast.error("Date & time required"); return; }
    setBusy(true);
    try {
      const payload = { ...form,
        scheduled_at: fromLocalInputValue(form.scheduled_at),
        duration_minutes: parseInt(form.duration_minutes || 60, 10) };
      if (isEdit) {
        await api.patch(`/admin/jidnyasa/sessions/${session.id}`, payload);
        toast.success("Session updated");
      } else {
        await api.post("/admin/jidnyasa/sessions", payload);
        toast.success("Session created");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} className="card-elevated p-6 relative"
          data-testid="admin-jidnyasa-form">
      <button type="button" onClick={onClose}
               className="absolute top-4 right-4 p-1 rounded-full hover:bg-white"
               data-testid="admin-jidnyasa-form-close">
        <X className="w-4 h-4"/>
      </button>
      <div className="font-display text-2xl mb-5">
        {isEdit ? "Edit session" : "New session"}
      </div>
      <div className="space-y-4">
        <div>
          <label className="text-xs text-muted-foreground">Topic</label>
          <input className="field text-base" value={form.topic}
                  onChange={(e) => set("topic", e.target.value)}
                  placeholder="e.g. How does the brain remember stuff?"
                  data-testid="admin-jidnyasa-topic"/>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Description (optional)</label>
          <textarea className="field text-sm min-h-[72px] resize-none" value={form.description}
                     onChange={(e) => set("description", e.target.value)}
                     placeholder="One-line hook for parents…"
                     data-testid="admin-jidnyasa-description"/>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground">Date & time</label>
            <input type="datetime-local" className="field text-sm" value={form.scheduled_at}
                    onChange={(e) => set("scheduled_at", e.target.value)}
                    data-testid="admin-jidnyasa-datetime"/>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Duration (min)</label>
            <input type="number" className="field text-sm" value={form.duration_minutes}
                    onChange={(e) => set("duration_minutes", e.target.value)}
                    min="15" max="240"
                    data-testid="admin-jidnyasa-duration"/>
          </div>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Meeting URL (Zoom / GMeet) — never shown to unsubscribed users</label>
          <input className="field text-sm" value={form.meeting_url}
                  onChange={(e) => set("meeting_url", e.target.value)}
                  placeholder="https://zoom.us/j/..."
                  data-testid="admin-jidnyasa-meeting-url"/>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground">Recording URL (post-session)</label>
            <input className="field text-sm" value={form.recording_url}
                    onChange={(e) => set("recording_url", e.target.value)}
                    placeholder="https://…"
                    data-testid="admin-jidnyasa-recording-url"/>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Materials URL (optional)</label>
            <input className="field text-sm" value={form.materials_url}
                    onChange={(e) => set("materials_url", e.target.value)}
                    placeholder="Drive / Notion / PDF link"
                    data-testid="admin-jidnyasa-materials-url"/>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground">Status</label>
            <select className="field text-sm" value={form.status}
                     onChange={(e) => set("status", e.target.value)}
                     data-testid="admin-jidnyasa-status">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.published}
                      onChange={(e) => set("published", e.target.checked)}
                      data-testid="admin-jidnyasa-published"/>
              Published (visible to parents)
            </label>
          </div>
        </div>
      </div>
      <button disabled={busy} className="btn-primary mt-6" data-testid="admin-jidnyasa-save">
        {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Save className="w-4 h-4"/> {isEdit ? "Update session" : "Create session"}</>}
      </button>
    </form>
  );
}

export default function AdminJidnyasa() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} for new | session object

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/jidnyasa/sessions");
      setSessions(r.data);
    } catch (e) {
      toast.error("Could not load sessions");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this session? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/jidnyasa/sessions/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div className="max-w-4xl">
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-start gap-3">
          <Video className="w-6 h-6 text-[hsl(var(--neu-orange))] mt-1"/>
          <div>
            <h1 className="font-display text-3xl">Jidnyasa · Live sessions</h1>
            <p className="text-sm text-muted-foreground">
              Manage every live weekly class. Meeting URLs are stored server-side and never exposed to non-subscribers.
            </p>
          </div>
        </div>
        {!editing && (
          <button onClick={() => setEditing({})} className="btn-primary text-sm"
                  data-testid="admin-jidnyasa-new-btn">
            <Plus className="w-4 h-4"/> New session
          </button>
        )}
      </div>

      {editing !== null && (
        <div className="mb-6">
          <SessionForm session={editing.id ? editing : null}
                        onSaved={() => { setEditing(null); load(); }}
                        onClose={() => setEditing(null)}/>
        </div>
      )}

      {loading ? (
        <div className="text-muted-foreground">Loading…</div>
      ) : sessions.length === 0 ? (
        <div className="card-soft p-8 text-center text-muted-foreground">
          No sessions yet. Click <b>New session</b> to schedule the first one.
        </div>
      ) : (
        <div className="space-y-3">
          {sessions.map((s) => (
            <div key={s.id} className="card-soft p-5 flex flex-col gap-4"
                  data-testid={`admin-jidnyasa-row-${s.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
                    s.status === "live" ? "bg-[hsl(var(--neu-orange))] text-white" :
                    s.status === "past" ? "bg-black/10 text-muted-foreground" :
                    s.status === "cancelled" ? "bg-destructive/10 text-destructive" :
                    "bg-[hsl(var(--neu-green))]/15 text-[hsl(var(--neu-green))]"
                  }`}>{s.status}</span>
                  {!s.published && (
                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-black/10 text-muted-foreground">
                      draft
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground">
                    {new Date(s.scheduled_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}
                  </span>
                </div>
                <div className="font-display text-lg mt-1">{s.topic}</div>
                <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-3">
                  {s.meeting_url && <span>🎥 meeting set</span>}
                  {s.recording_url && <span>▶ recording</span>}
                  {s.materials_url && <span>📎 materials</span>}
                  {(s.attachments?.length > 0) && <span>📄 {s.attachments.length} file(s)</span>}
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => setEditing(s)}
                         className="btn-ghost text-sm px-3 py-1.5"
                         data-testid={`admin-jidnyasa-edit-${s.id}`}>
                  Edit
                </button>
                <button onClick={() => del(s.id)}
                         className="text-sm px-3 py-1.5 rounded-full text-destructive hover:bg-destructive/10 inline-flex items-center gap-1"
                         data-testid={`admin-jidnyasa-delete-${s.id}`}>
                  <Trash2 className="w-3.5 h-3.5"/> Delete
                </button>
              </div>
              </div>
              <JidnyasaAttachments sessionId={s.id} backendUrl={BACKEND_URL}
                                    onChange={(atts) => { s.attachments = atts; }}/>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
